import { useEffect, useState } from "react";
import { AdminScreen } from "./admin/AdminScreen";
import { DisplayScreen } from "./display/DisplayScreen";
import { fetchSnapshot } from "./api";
import { loadCachedSnapshot, saveCachedSnapshot } from "./shared/cache";
import { checkDisplayFrontendAssetRefresh } from "./frontendAssets";
import { createSampleSnapshot } from "./shared/sampleData";
import type { PublicDisplaySnapshot } from "./shared/types";

export const DISPLAY_SNAPSHOT_REFRESH_MS = 60_000;

export function App() {
  const [snapshot, setSnapshot] = useState<PublicDisplaySnapshot>(() => {
    if (typeof window === "undefined") {
      return createSampleSnapshot();
    }

    return loadCachedSnapshot(window.localStorage) ?? createSampleSnapshot();
  });
  const [now, setNow] = useState(() => new Date());
  const isAdmin = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isAdmin) {
      return;
    }

    let cancelled = false;

    async function refresh() {
      try {
        const nextSnapshot = await fetchSnapshot();
        if (!cancelled) {
          setSnapshot(nextSnapshot);
          saveCachedSnapshot(window.localStorage, nextSnapshot);
        }
      } catch {
        const cached = loadCachedSnapshot(window.localStorage);
        if (!cancelled && cached) {
          setSnapshot(cached);
        }
      }
    }

    void refresh();
    const timer = window.setInterval(refresh, DISPLAY_SNAPSHOT_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin || typeof window === "undefined") {
      return;
    }

    let cancelled = false;

    async function refreshWhenFrontendChanges() {
      try {
        await checkDisplayFrontendAssetRefresh({
          documentRef: window.document,
          reload: () => {
            if (!cancelled) {
              window.location.reload();
            }
          }
        });
      } catch {
        // Keep the TV display running if the version check fails while offline.
      }
    }

    void refreshWhenFrontendChanges();
    const timer = window.setInterval(refreshWhenFrontendChanges, 60_000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isAdmin]);

  return isAdmin ? <AdminScreen initialSnapshot={snapshot} /> : <DisplayScreen snapshot={snapshot} now={now} />;
}
